import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ImportInstructions = () => {
  const [activeTab, setActiveTab] = useState('format');

  const sampleData = `Full Name,CPF,Voter ID,Email,Phone,Date of Birth,Address,City,State,ZIP Code
"John Silva Santos","123.456.789-00","1234567890123","john.santos@email.com","(11) 99999-9999","1985-03-15","Rua das Flores, 123","São Paulo","SP","01234-567"
"Maria Oliveira Costa","987.654.321-00","9876543210987","maria.costa@email.com","(21) 88888-8888","1990-07-22","Av. Copacabana, 456","Rio de Janeiro","RJ","22070-001"
"Carlos Pereira Lima","456.789.123-00","4567891230456","carlos.lima@email.com","(31) 77777-7777","1978-11-08","Rua Minas, 789","Belo Horizonte","MG","30112-000"`;

  const downloadSampleFile = () => {
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'member_import_template.csv';
    document.body?.appendChild(a);
    a?.click();
    window.URL?.revokeObjectURL(url);
    document.body?.removeChild(a);
  };

  const tabs = [
    { id: 'format', label: 'Formato do arquivo', icon: 'FileText' },
    { id: 'fields', label: 'Campos obrigatórios', icon: 'List' },
    { id: 'validation', label: 'Regras de validação', icon: 'Shield' },
    { id: 'tips', label: 'Dicas de importação', icon: 'Lightbulb' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'format':
        return (
          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Formatos de arquivo suportados</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-muted rounded-lg text-center">
                <Icon name="FileText" size={32} className="text-primary mx-auto mb-2" />
                <div className="font-medium text-sm">CSV Files</div>
                <div className="text-xs text-muted-foreground">.csv</div>
              </div>
              <div className="p-4 bg-muted rounded-lg text-center">
                <Icon name="FileSpreadsheet" size={32} className="text-success mx-auto mb-2" />
                <div className="font-medium text-sm">Excel 97-2003</div>
                <div className="text-xs text-muted-foreground">.xls</div>
              </div>
              <div className="p-4 bg-muted rounded-lg text-center">
                <Icon name="FileSpreadsheet" size={32} className="text-success mx-auto mb-2" />
                <div className="font-medium text-sm">Excel 2007+</div>
                <div className="text-xs text-muted-foreground">.xlsx</div>
              </div>
            </div>
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <h5 className="font-medium text-primary mb-2">Baixar template de exemplo</h5>
              <p className="text-sm text-muted-foreground mb-3">
                Use nosso template pré-formatado para garantir a importação correta dos dados.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={downloadSampleFile}
                iconName="Download"
                iconPosition="left"
              >
                Baixar template CSV
              </Button>
            </div>
          </div>
        );

      case 'fields':
        return (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-foreground mb-3 flex items-center">
                  <Icon name="AlertCircle" size={16} className="text-error mr-2" />
                  Campos obrigatórios
                </h4>
                <div className="space-y-2">
                  {[
                    { field: 'Nome completo', example: 'John Silva Santos' },
                    { field: 'CPF', example: '123.456.789-00' },
                    { field: 'Título de eleitor', example: '1234567890123' },
                    { field: 'E-mail', example: 'john@email.com' },
                    { field: 'Telefone', example: '(11) 99999-9999' }
                  ]?.map((item, index) => (
                    <div key={index} className="p-3 bg-muted rounded border-l-4 border-error">
                      <div className="font-medium text-sm">{item?.field}</div>
                      <div className="text-xs text-muted-foreground">Exemplo: {item?.example}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-3 flex items-center">
                  <Icon name="Info" size={16} className="text-primary mr-2" />
                  Campos opcionais
                </h4>
                <div className="space-y-2">
                  {[
                    { field: 'Data de nascimento', example: '1985-03-15' },
                    { field: 'Endereço', example: 'Rua das Flores, 123' },
                    { field: 'Cidade', example: 'São Paulo' },
                    { field: 'Estado', example: 'SP' },
                    { field: 'CEP', example: '01234-567' }
                  ]?.map((item, index) => (
                    <div key={index} className="p-3 bg-muted rounded border-l-4 border-primary">
                      <div className="font-medium text-sm">{item?.field}</div>
                      <div className="text-xs text-muted-foreground">Example: {item?.example}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'validation':
        return (
          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Regras de validação de dados</h4>
            <div className="space-y-3">
              {[
                {
                  rule: 'Formato do CPF',
                  description: 'Deve estar no formato XXX.XXX.XXX-XX ou XXXXXXXXXXX',
                  icon: 'Hash'
                },
                {
                  rule: 'Formato do Título de Eleitor',
                  description: 'Deve ter exatamente 13 dígitos',
                  icon: 'CreditCard'
                },
                {
                  rule: 'Formato do E-mail',
                  description: 'Deve ser um e-mail válido',
                  icon: 'Mail'
                },
                {
                  rule: 'Formato do Telefone',
                  description: 'Formato brasileiro: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX',
                  icon: 'Phone'
                },
                {
                  rule: 'Formato de data',
                  description: 'Data de nascimento no formato YYYY-MM-DD',
                  icon: 'Calendar'
                },
                {
                  rule: 'Detecção de duplicados',
                  description: 'CPF e Título de Eleitor devem ser únicos em todos os registros',
                  icon: 'Copy'
                }
              ]?.map((item, index) => (
                <div key={index} className="flex items-start p-3 bg-muted rounded-lg">
                  <Icon name={item?.icon} size={20} className="text-primary mr-3 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm text-foreground">{item?.rule}</div>
                    <div className="text-xs text-muted-foreground mt-1">{item?.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'tips':
        return (
          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Dicas e boas práticas</h4>
            <div className="space-y-3">
              {[
                {
                  tip: 'Use a primeira linha para os cabeçalhos',
                  description: 'Garanta que a primeira linha contenha os nomes exatamente como no template'
                },
                {
                  tip: 'Verifique duplicados antes de importar',
                  description: 'Revise dados duplicados de CPF ou Título para evitar erros'
                },
                {
                  tip: 'Valide os formatos',
                  description: 'Garanta que CPF, telefones e e-mails estejam no formato brasileiro correto'
                },
                {
                  tip: 'Mantenha o arquivo abaixo de 10MB',
                  description: 'Arquivos grandes demoram mais. Considere dividir conjuntos muito grandes'
                },
                {
                  tip: 'Use codificação UTF-8',
                  description: 'Salve arquivos CSV com UTF-8 para preservar caracteres especiais'
                },
                {
                  tip: 'Revise antes da importação final',
                  description: 'Sempre revise a prévia para evitar problemas de formatação'
                }
              ]?.map((item, index) => (
                <div key={index} className="flex items-start p-3 bg-success/5 border border-success/20 rounded-lg">
                  <Icon name="CheckCircle" size={20} className="text-success mr-3 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm text-foreground">{item?.tip}</div>
                    <div className="text-xs text-muted-foreground mt-1">{item?.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center">
          <Icon name="HelpCircle" size={20} className="mr-2" />
          Import Instructions
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Follow these guidelines to ensure successful member data import
        </p>
      </div>
      {/* Tabs */}
      <div className="border-b border-border">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition-colors duration-200 ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </nav>
      </div>
      {/* Tab Content */}
      <div className="p-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ImportInstructions;